import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Modal, Paragraph, Anchor, Heading, List, Text, TriggerText } from "@zillow/constellation-10";
import { useState, useMemo, useEffect } from "react";
import { formatIntoDollars, BRANDING, getLegalDisclosureText, getNationalConformingLoanLimits } from "./constants.esm.js";
import format from "../../node_modules/date-fns/esm/format/index.esm.js";
const LegalDisclosuresModal = ({
  classification,
  rates
}) => {
  const [nationalConformingLoanLimits, setNationalConformingLoanLimits] = useState({});
  const formatLimit = (limit) => limit ? formatIntoDollars(limit) : "--";
  const minLimit = formatLimit(nationalConformingLoanLimits.minLimit);
  const maxLimit = formatLimit(nationalConformingLoanLimits.maxLimit);
  const RATE_DETAILS_ID = "LegalDisclosuresModal_rate_details";
  const {
    refinance
  } = classification;
  const lastUpdated = useMemo(() => {
    const rateData = Object.values(rates).find(({
      today
    }) => !!(today == null ? void 0 : today.time));
    if (rateData) {
      const date = new Date(rateData.today.time);
      return format(date, "MM/dd/yyyy");
    }
    return null;
  }, [rates]);
  useEffect(() => {
    const loadLimits = async () => {
      setNationalConformingLoanLimits(await getNationalConformingLoanLimits());
    };
    loadLimits();
  }, []);
  return /* @__PURE__ */ jsx(Modal, { size: "md", trigger: /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", children: /* @__PURE__ */ jsx(TriggerText, { children: "See legal disclosures." }) }), header: /* @__PURE__ */ jsx(Heading, { level: 6, children: "Legal Disclosures" }), body: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Paragraph, { css: {
      marginBottom: "default"
    }, children: [
      "Representative rates are based upon national or state specific averages from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. Representative rates",
      " ",
      lastUpdated && `are valid as of ${lastUpdated} and`,
      " ",
      "assume a minimum credit score of 740 and loan-to-value ratio of 80%, except for FHA and VA loans which assume a minimum credit score of 680-739 and loan-to-value ratio of 96.5% and 100%, respectively. The Jumbo loan amount shown exceeds the highest conforming loan limit in most state or city locations for which rates are being provided. Currently, a Jumbo loan exceeds ",
      minLimit,
      " for a single-family home in all states (except Hawaii and Alaska, and a few federally designated markets where the limit is ",
      maxLimit,
      "). Estimated monthly payment amounts displayed are based upon principal and interest only, and taxes and insurance are not included in this estimate; the actual payment obligation may be greater. Not all borrowers will qualify for these rates. Final loan approval is subject to criteria established by a lender, including satisfactory appraisal, title review, and underwriting determination, among other criteria. Rates subject to change without notice. Restrictions apply.",
      " ",
      /* @__PURE__ */ jsx(Anchor, { href: `#${RATE_DETAILS_ID}`, children: "Click here to learn more details about the rates displayed." })
    ] }),
    /* @__PURE__ */ jsx(Paragraph, { css: {
      marginBottom: "default"
    }, children: "NOTE: Mortgage rate trends are based on historical rate data and the inputs that you select. Changes to inputs will affect the rate trends shown. Mortgage rate trends displayed are for informational purposes only and represent rate trends over time and are based upon national or state specific averages from lenders quoting on Zillow for preliminary research purposes only." }),
    /* @__PURE__ */ jsxs(Paragraph, { css: {
      marginBottom: "default"
    }, children: [
      "Mortgage rates are displayed through",
      " ",
      BRANDING.ZGMI_FULL_NAME,
      " a licensed mortgage broker,",
      " ",
      BRANDING.ZGMI_NMLS_ID,
      ". ",
      BRANDING.ZGMI_INC,
      " does not make loans and this is not a commitment to lend. See current list of state licenses and disclosures here. Please note: Adjustable-rate mortgages (ARMs) are loans with interest rates that change after an initial fixed-rate period."
    ] }),
    /* @__PURE__ */ jsx(Paragraph, { css: {
      marginBottom: "default"
    }, children: "ZGMI does not aid or assist consumers in obtaining loans, solicit consumers or lenders for loans, offer or negotiate terms of loans, make loans or credit decisions in connection with loans, take applications for credit, or issue pre-approval letters. ZGMI allows consumers to provide certain information to lenders. ZGMI does not guarantee that the loan terms or rates offered and made available by lenders are the best terms or lowest rates available in the market." }),
    /* @__PURE__ */ jsx(Heading, { level: 2, textStyle: "heading-xs", id: RATE_DETAILS_ID, children: "More details about the rates displayed" }),
    /* @__PURE__ */ jsx(List.Root, { children: Object.entries(rates).filter(([, {
      today
    }]) => !!today.apr).map(([loanType, {
      todayPayment,
      today,
      name
    }]) => /* @__PURE__ */ jsx(List.Item, { children: getLegalDisclosureText(todayPayment, today, name, refinance) }, loanType)) })
  ] }) });
};
export {
  LegalDisclosuresModal as default
};
